import { useEffect, useMemo, useState } from "react";
import type { ColumnFilter } from "../utils/apply-filters";
import type { ColumnKind } from "../utils/derive-column-kind";

interface IProps {
	columnKind: ColumnKind;
	uniqueValues: string[];
	filter: ColumnFilter | null;
	onChange: (next: ColumnFilter | null) => void;
}

const SearchIcon = () => (
	<svg
		width="16"
		height="16"
		viewBox="0 0 16 16"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		aria-hidden
	>
		<circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5" />
		<path
			d="M11 11L14 14"
			stroke="currentColor"
			strokeWidth="1.5"
			strokeLinecap="round"
		/>
	</svg>
);

const CheckIcon = () => (
	<svg
		width="12"
		height="12"
		viewBox="0 0 12 12"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		aria-hidden
	>
		<path
			d="M2.5 6L5 8.5L9.5 4"
			stroke="white"
			strokeWidth="1.75"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

const parseBound = (raw: string): number | null => {
	const trimmed = raw.trim();
	if (trimmed === "") return null;
	const n = Number(trimmed);
	return Number.isFinite(n) ? n : null;
};

const NumberRangeUI = ({
	filter,
	onChange,
}: {
	filter: ColumnFilter | null;
	onChange: (next: ColumnFilter | null) => void;
}) => {
	const current =
		filter && filter.kind === "range"
			? filter
			: { kind: "range" as const, min: null, max: null };
	const [minStr, setMinStr] = useState<string>(
		current.min === null ? "" : String(current.min),
	);
	const [maxStr, setMaxStr] = useState<string>(
		current.max === null ? "" : String(current.max),
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: intentionally keyed only on external min/max
	useEffect(() => {
		if (parseBound(minStr) !== current.min) {
			setMinStr(current.min === null ? "" : String(current.min));
		}
		if (parseBound(maxStr) !== current.max) {
			setMaxStr(current.max === null ? "" : String(current.max));
		}
	}, [current.min, current.max]);

	const commit = (nextMin: string, nextMax: string) => {
		const min = parseBound(nextMin);
		const max = parseBound(nextMax);
		if (min === null && max === null) {
			onChange(null);
		} else {
			onChange({ kind: "range", min, max });
		}
	};

	return (
		<div className="table-filter-menu-range" role="group">
			<label className="table-filter-menu-range-field">
				<span className="table-filter-menu-range-label">Min</span>
				<input
					type="number"
					inputMode="decimal"
					value={minStr}
					onChange={(e) => {
						setMinStr(e.target.value);
						commit(e.target.value, maxStr);
					}}
					aria-label="Minimum value"
					placeholder="—"
				/>
			</label>
			<label className="table-filter-menu-range-field">
				<span className="table-filter-menu-range-label">Max</span>
				<input
					type="number"
					inputMode="decimal"
					value={maxStr}
					onChange={(e) => {
						setMaxStr(e.target.value);
						commit(minStr, e.target.value);
					}}
					aria-label="Maximum value"
					placeholder="—"
				/>
			</label>
		</div>
	);
};

export const TableFilterMenu = ({
	columnKind,
	uniqueValues,
	filter,
	onChange,
}: IProps) => {
	const [search, setSearch] = useState("");

	const allValues = useMemo(
		() => new Set<string>(uniqueValues),
		[uniqueValues],
	);
	const selectedValues: Set<string> =
		filter && filter.kind === "values" ? filter.selected : allValues;

	const visibleValues = useMemo(() => {
		if (columnKind !== "text") return uniqueValues;
		if (!search.trim()) return uniqueValues;
		const needle = search.toLowerCase();
		return uniqueValues.filter((v) => v.toLowerCase().includes(needle));
	}, [columnKind, uniqueValues, search]);

	const emitValues = (next: Set<string>) => {
		if (next.size === uniqueValues.length || next.size === 0) onChange(null);
		else onChange({ kind: "values", selected: next });
	};

	const toggleValue = (value: string) => {
		const next = new Set(selectedValues);
		if (next.has(value)) next.delete(value);
		else next.add(value);
		emitValues(next);
	};

	const allVisibleChecked =
		visibleValues.length > 0 &&
		visibleValues.every((v) => selectedValues.has(v));

	const handleSelectAll = () => {
		if (visibleValues.length === 0) return;
		const next = new Set(selectedValues);
		if (allVisibleChecked) {
			visibleValues.forEach((v) => next.delete(v));
		} else {
			visibleValues.forEach((v) => next.add(v));
		}
		emitValues(next);
	};

	return (
		<div
			className="table-filter-menu"
			role="dialog"
			aria-label="Filter column"
			onMouseDown={(e) => e.stopPropagation()}
		>
			<div className="table-filter-menu-head">
				<span className="table-filter-menu-title">Filter</span>
				{columnKind === "text" && (
					<button
						type="button"
						className="table-filter-menu-select-all"
						onClick={handleSelectAll}
					>
						Select all
					</button>
				)}
			</div>
			{columnKind === "number" ? (
				<NumberRangeUI filter={filter} onChange={onChange} />
			) : (
				<>
					<label className="table-filter-menu-search">
						<span className="table-filter-menu-search-icon" aria-hidden>
							<SearchIcon />
						</span>
						<input
							type="text"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							placeholder="Search"
							aria-label="Search values"
						/>
					</label>
					<div className="table-filter-menu-list" role="group">
						{visibleValues.length === 0 ? (
							<div className="table-filter-menu-empty">No values</div>
						) : (
							visibleValues.map((value) => {
								const isChecked = selectedValues.has(value);
								return (
									<label key={value} className="table-filter-menu-item">
										<span
											className="table-filter-menu-checkbox"
											data-checked={isChecked ? "" : undefined}
											aria-hidden
										>
											{isChecked && <CheckIcon />}
										</span>
										<input
											type="checkbox"
											className="tw-sr-only"
											checked={isChecked}
											onChange={() => toggleValue(value)}
										/>
										<span
											className="table-filter-menu-item-label"
											title={value}
										>
											{value || "(empty)"}
										</span>
									</label>
								);
							})
						)}
					</div>
				</>
			)}
		</div>
	);
};
